import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { getUserGroups } from "../../auth/permissions";

const BASE = (process.env.REACT_APP_SUPPLIER_ACK_API_URL || "").replace(/\/$/, "");
const field = { padding: "10px 12px", border: "1px solid #cbd5e1", borderRadius: 9, minWidth: 170, fontSize: 13 };
const button = { padding: "10px 14px", background: "#17385c", color: "white", border: 0, borderRadius: 9, cursor: "pointer", fontWeight: 700 };
const fmt = value => value ? new Date(value).toLocaleString("es-MX", {timeZone:"America/Cancun"}) : "—";
const safe = value => String(value ?? "");

export default function AcusesControlView() {
  const [records, setRecords] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [state, setState] = useState("TODOS");
  const [selected, setSelected] = useState(null);
  const isAdmin = getUserGroups().some(x => String(x).toLowerCase() === "admin");
  const load = useCallback(async (nextCursor = null, append = false) => {
    setLoading(true); setError("");
    try {
      const token = localStorage.getItem("accessToken") || "";
      if (!BASE.startsWith("https://")) throw new Error("Configurar REACT_APP_SUPPLIER_ACK_API_URL");
      if (!token) throw new Error("Inicia sesión para consultar los acuses.");
      const url = new URL(BASE + "/proveedor/acuses");
      url.searchParams.set("limit", "50");
      if (nextCursor) url.searchParams.set("cursor", nextCursor);
      const response = await fetch(url.toString(), {headers:{Authorization:`Bearer ${token}`}, cache:"no-store"});
      const data = await response.json();
      if (!response.ok || !data.ok) throw new Error(data.error || `HTTP ${response.status}`);
      setRecords(prev => append ? [...prev, ...(data.registros || [])] : (data.registros || []));
      setCursor(data.cursor || null);
    } catch (e) { setError(e.message || "Error de consulta"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { if (isAdmin) load(); }, [isAdmin, load]);
  const filtered = useMemo(() => records.filter(r => {
    const q = query.trim().toLowerCase();
    return (!q || [r.folioSolicitud,r.proveedorId,r.email,r.alertaId].some(v => safe(v).toLowerCase().includes(q))) &&
      (state === "TODOS" || r.acuseEstado === state);
  }), [records, query, state]);
  const confirmed = records.filter(r => r.acuseEstado === "CONFIRMADO").length;
  const pending = records.filter(r => r.acuseEstado === "PENDIENTE").length;
  async function downloadPdf(r) {
    const { jsPDF } = await import("jspdf");
    const doc = new jsPDF();
    let y = 22;
    const line = (label, value) => {
      const rows = doc.splitTextToSize(`${label}: ${safe(value)}`, 175);
      if (y + rows.length * 7 > 275) {doc.addPage(); y = 20;}
      doc.text(rows, 17, y); y += 7 * rows.length + 2;
    };
    doc.setFontSize(17); doc.text("IP DEPOT | Constancia de acuse",17,y); y += 12;
    doc.setFontSize(10);
    line("Folio", r.folioSolicitud);
    line("Proveedor", r.proveedorId);
    line("Destinatario", r.email);
    line("SES", r.sesEstado);
    line("Acuse", r.acuseEstado);
    line("Creado", fmt(r.creadoEn));
    line("SES aceptado", fmt(r.sesAceptadoEn));
    line("Confirmado", fmt(r.fechaConfirmacion));
    line("Identidad Cognito", r.confirmadoPorSub || "Sin confirmar");
    line("Hash de solicitud SHA-256", r.solicitudHash);
    y += 4; line("Detalle de oficinas y cantidades", "");
    (r.solicitudSnapshot?.oficinas || []).forEach((o,i) => {
      line(`Oficina ${i+1}`, o.oficinaNombre || o.oficinaId);
      line("Estado", o.estado);
      Object.entries(o.pedidoSugerido || {}).forEach(([item, qty]) => line(`   ${item}`,qty));
    });
    y += 4; line("Alcance", "Esta constancia acredita recepción de la solicitud, no entrega física de insumos ni una firma electrónica certificada.");
    doc.save(`Acuse-${safe(r.folioSolicitud).replace(/[^\w-]/g,"_")}.pdf`);
  }
  if (!isAdmin) return <main style={{padding:30}}>Acceso reservado al administrador.</main>;
  return <main style={{background:"#f3f6fa",minHeight:"80vh",padding:24,color:"#183153"}}>
    <header style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:12,flexWrap:"wrap"}}>
      <div><h1 style={{margin:0}}>Control de solicitudes y acuses</h1><p>Seguimiento de requerimientos enviados a proveedores · Consulta administrativa</p></div>
      <div><Link to="/insumos-produccion" style={{marginRight:14}}>Volver a Insumos</Link><button style={button} disabled={loading} onClick={()=>load()}>Actualizar</button></div>
    </header>
    <div style={{display:"flex",gap:14,flexWrap:"wrap",margin:"22px 0"}}>
      {[["Registros cargados",records.length],["Confirmados",confirmed],["Pendientes",pending]].map(([label,value])=><div key={label} style={{background:"white",padding:20,borderRadius:12,minWidth:170,border:"1px solid #e2e8f0"}}><small>{label}</small><div style={{fontSize:29,fontWeight:800}}>{value}</div></div>)}
    </div>
    <div style={{display:"flex",gap:10,flexWrap:"wrap",marginBottom:16}}>
      <input style={field} value={query} onChange={e=>setQuery(e.target.value)} placeholder="Buscar folio, proveedor o correo" aria-label="Buscar acuses" />
      <select style={field} value={state} onChange={e=>setState(e.target.value)}><option>TODOS</option><option>PENDIENTE</option><option>CONFIRMADO</option></select>
    </div>
    {error && <p role="alert" style={{color:"#b91c1c"}}>{error}</p>}
    <div style={{overflowX:"auto",background:"white",border:"1px solid #e2e8f0",borderRadius:12}}>
      <table style={{width:"100%",borderCollapse:"collapse",fontSize:13,textAlign:"left"}}><thead><tr>{["Folio","Proveedor","Destinatario","SES","Acuse","Confirmación","Acciones"].map(x=><th key={x} style={{padding:13,borderBottom:"1px solid #e2e8f0"}}>{x}</th>)}</tr></thead>
      <tbody>{filtered.map(r=><tr key={r.alertaId}>
        {[r.folioSolicitud,r.proveedorId,r.email,r.sesEstado,r.acuseEstado,fmt(r.fechaConfirmacion)].map((v,i)=><td key={i} style={{padding:13,borderBottom:"1px solid #edf2f7",whiteSpace:"nowrap"}}>{v || "—"}</td>)}
        <td style={{padding:12,borderBottom:"1px solid #edf2f7",whiteSpace:"nowrap"}}><button onClick={()=>setSelected(r)} style={{marginRight:10}}>Detalle</button><button onClick={()=>downloadPdf(r)}>PDF</button></td>
      </tr>)}{!filtered.length && <tr><td colSpan="7" style={{padding:25,textAlign:"center"}}>Sin registros en los resultados cargados.</td></tr>}</tbody></table>
    </div>
    {cursor && <button style={{...button,marginTop:16}} disabled={loading} onClick={()=>load(cursor,true)}>Cargar más registros</button>}
    <p style={{color:"#64748b",fontSize:12}}>Los indicadores cuentan únicamente los registros cargados. El estado de acuse no equivale a recepción física de mercancía.</p>
    {selected && <section style={{background:"white",marginTop:20,padding:20,borderRadius:12,border:"1px solid #cbd5e1"}}><div style={{display:"flex",justifyContent:"space-between"}}><h2>Detalle · {selected.folioSolicitud}</h2><button onClick={()=>setSelected(null)}>Cerrar</button></div>
      <p><strong>Proveedor:</strong> {selected.proveedorId} · <strong>Destinatario:</strong> {selected.email}</p>
      <p><strong>Confirmó:</strong> {selected.confirmadoPorSub || "Pendiente"} · <strong>Fecha:</strong> {fmt(selected.fechaConfirmacion)}</p>
      {(selected.solicitudSnapshot?.oficinas || []).map((o,i)=><div key={i} style={{padding:12,borderTop:"1px solid #e2e8f0"}}><strong>{o.oficinaNombre || o.oficinaId}</strong> · {o.estado}<div>{Object.entries(o.pedidoSugerido || {}).map(([k,v])=><span key={k} style={{display:"inline-block",marginRight:18}}>{k}: {v}</span>)}</div></div>)}
      <button style={button} onClick={()=>downloadPdf(selected)}>Descargar comprobante PDF</button>
    </section>}
  </main>;
}
